import React, { useMemo, useState } from 'react';
import { supabase } from '../services/supabase';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasKidsChurchStaffRole } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  max-width: 520px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  min-height: 90px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-weight: 600;
`;

const Button = styled.button`
  padding: 0.6rem 1.1rem;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  color: #e53e3e;
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 180px;
  overflow: auto;
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: #1f2937;

  &:hover {
    background: #f1f5ff;
  }
`;

interface Household {
  id: string;
  name: string;
}

const KidsChurchChildFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [emergencyRelationship, setEmergencyRelationship] = useState('');
  const [allergies, setAllergies] = useState('');
  const [medicalConditions, setMedicalConditions] = useState('');
  const [specialNeeds, setSpecialNeeds] = useState('');
  const [teacherNotes, setTeacherNotes] = useState('');
  const [isStaff, setIsStaff] = useState(false);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [householdSearch, setHouseholdSearch] = useState('');
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(null);
  const isTeacherRoleStaff = hasKidsChurchStaffRole(profile?.roles ?? user?.user_metadata?.roles);

  React.useEffect(() => {
    const loadStaffStatus = async () => {
      const userId = user?.id;
      if (!userId) {
        setIsStaff(isTeacherRoleStaff);
        return;
      }
      const { data: staffRecord } = await supabase
        .from('kids_church_staff')
        .select('id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      setIsStaff(Boolean(staffRecord?.id) || isTeacherRoleStaff);
    };
    void loadStaffStatus();
  }, [isTeacherRoleStaff, user?.id]);

  React.useEffect(() => {
    const loadHouseholds = async () => {
      if (!isStaff) return;
      const PAGE = 1000;
      const all: Household[] = [];
      let from = 0;
      while (true) {
        const to = from + PAGE - 1;
        const { data: chunk } = await supabase
          .from('households')
          .select('id, name')
          .order('name', { ascending: true })
          .range(from, to);
        const rows = (chunk as Household[]) ?? [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }
      setHouseholds(all);
    };
    loadHouseholds();
  }, [isStaff]);

  const filteredHouseholds = useMemo(() => {
    const term = householdSearch.trim().toLowerCase();
    if (!term) return [];
    return households.filter((household) => household.name.toLowerCase().includes(term));
  }, [householdSearch, households]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('create-child-account', {
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: dob || null,
          emergencyContactName: emergencyName.trim() || null,
          emergencyContactPhone: emergencyPhone.trim() || null,
          emergencyContactRelationship: emergencyRelationship.trim() || null,
          allergies: allergies.trim() || null,
          medicalConditions: medicalConditions.trim() || null,
          specialNeeds: specialNeeds.trim() || null,
          teacherNotes: teacherNotes.trim() || null,
          householdId: selectedHouseholdId,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      navigate('/kids-church');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Page>
      <h1 style={{ marginBottom: '1rem' }}>Add New Child</h1>
      <FormCard>
        <Form onSubmit={handleSubmit}>
        <Label>
          First Name
          <Input
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
          />
        </Label>

        <Label>
          Last Name
          <Input
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
          />
        </Label>

        <Label>
          Date of Birth
          <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </Label>

        {isStaff ? (
          <Label>
            Add to Household (optional)
            <Input
              value={householdSearch}
              onChange={(e) => {
                setHouseholdSearch(e.target.value);
                setSelectedHouseholdId(null);
              }}
              placeholder="Search households..."
            />
            {householdSearch.trim().length > 0 && filteredHouseholds.length > 0 ? (
              <DropdownList>
                {filteredHouseholds.map((household) => (
                  <DropdownItem
                    key={household.id}
                    onClick={() => {
                      setSelectedHouseholdId(household.id);
                      setHouseholdSearch(household.name);
                    }}
                  >
                    {household.name}
                  </DropdownItem>
                ))}
              </DropdownList>
            ) : null}
          </Label>
        ) : null}

        <h3>Emergency Contact</h3>

        <Label>
          Name
          <Input
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
            placeholder="Jane Doe"
          />
        </Label>

        <Label>
          Phone
          <Input
            value={emergencyPhone}
            onChange={(e) => setEmergencyPhone(e.target.value)}
            placeholder="555-1234"
          />
        </Label>

        <Label>
          Relationship to Child
          <Input
            value={emergencyRelationship}
            onChange={(e) => setEmergencyRelationship(e.target.value)}
            placeholder="Mother / Father / Guardian"
          />
        </Label>

        <h3>Health & Special Needs</h3>

        <Label>
          Allergies
          <Input
            value={allergies}
            onChange={(e) => setAllergies(e.target.value)}
            placeholder="Peanuts, dairy, etc."
          />
        </Label>

        <Label>
          Medical Conditions
          <Input
            value={medicalConditions}
            onChange={(e) => setMedicalConditions(e.target.value)}
            placeholder="Asthma, diabetes, etc."
          />
        </Label>

        <Label>
          Special Needs
          <Input
            value={specialNeeds}
            onChange={(e) => setSpecialNeeds(e.target.value)}
            placeholder="Behavioral, developmental, etc."
          />
        </Label>

        <Label>
          Notes for Teacher
          <TextArea
            value={teacherNotes}
            onChange={(e) => setTeacherNotes(e.target.value)}
            placeholder="Anything the teacher should know..."
          />
        </Label>

        {mutation.isError && (
          <ErrorText>Failed to add child. Please try again.</ErrorText>
        )}

          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Form>
      </FormCard>
    </Page>
  );
};

export default KidsChurchChildFormPage;
